import { HandoffManagement } from "@/components/HandoffManagement";
import { useContext, useState, useEffect } from "react";
import { useStytchMember } from "@stytch/react/b2b";
import { trpc } from "@/trpc";
import MerchantContext from "@/contexts/MerchantContext";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import posthog from "@/lib/posthog";

const AssignmentContainer = () => {
  const { merchantId, isLoading, merchantType } = useContext(MerchantContext);
  const { member } = useStytchMember();
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const utils = trpc.useUtils();

  const employees = trpc.employees.list.useQuery(
    { merchantId },
    {
      enabled: !!merchantId,
    }
  );

  const merchantConfig = trpc.merchants.get.useQuery(
    { id: merchantId },
    {
      enabled: !!merchantId,
    }
  );
  const restrictEmployeeStatusChanges = merchantConfig.data?.restrictEmployeeStatusChanges ?? false;
  const employeeStatusSuperEmails = merchantConfig.data?.employeeStatusSuperEmails ?? [];
  const callerEmail = (member?.email_address ?? "").toLowerCase();

  const createEmployee = trpc.employees.createEmployee.useMutation({
    onSuccess: () => {
      employees.refetch();
    },
  });

  const updateEmployee = trpc.employees.updateEmployee.useMutation({
    onSuccess: () => {
      employees.refetch();
    },
  });

  const toggleEmployeeStatus = trpc.employees.toggleStatus.useMutation({
    onSuccess: (_data, variables) => {
      const toggledEmployee = employees.data?.find((e) => e.id === variables.employeeId);
      posthog.capture(
        variables.status === "active" ? "employee activated" : "employee deactivated",
        {
          merchant_id: merchantId,
          employee_id: variables.employeeId,
          employee_name: toggledEmployee?.name,
          triggered_by_email: member?.email_address,
          triggered_by_name: member?.name,
        }
      );
      employees.refetch();
    },
  });

  const assignmentRulesets = trpc.assignmentRulesets.list.useQuery(
    { merchantId },
    {
      enabled: !!merchantId,
    }
  );

  const createAssignmentRuleset = trpc.assignmentRulesets.create.useMutation({
    onSuccess: () => {
      assignmentRulesets.refetch();
    },
  });

  const updateAssignmentRuleset = trpc.assignmentRulesets.update.useMutation({
    onSuccess: () => {
      assignmentRulesets.refetch();
    },
  });

  const addEmployeesToAssignmentRuleset = trpc.assignmentRulesets.addEmployees.useMutation({
    onSuccess: () => {
      assignmentRulesets.refetch();
    },
  });

  const removeEmployeesFromAssignmentRuleset = trpc.assignmentRulesets.removeEmployees.useMutation({
    onSuccess: () => {
      assignmentRulesets.refetch();
    },
  });

  const cityNeighborhoods = trpc.assignmentRulesets.getCityNeighborhoods.useQuery(
    { merchantId },
    {
      enabled: !!merchantId,
    }
  );

  const branches = trpc.assignmentRulesets.getBranches.useQuery(
    { merchantId },
    {
      enabled: !!merchantId,
    }
  );

  const transferCategories = trpc.assignmentRulesets.getTransferCategories.useQuery(
    { merchantId },
    {
      enabled: !!merchantId,
    }
  );

  const pipelines = trpc.pipeline.list.useQuery(
    { merchantId },
    {
      enabled: !!merchantId,
    }
  );

  // Agents list for transfer categories tab
  const agents = trpc.agents.list.useQuery(
    { merchantId },
    {
      enabled: !!merchantId,
    }
  );

  // Set first agent as selected by default
  useEffect(() => {
    if (agents.data && agents.data.length > 0 && !selectedAgentId) {
      setSelectedAgentId(agents.data[0].id);
    }
  }, [agents.data, selectedAgentId]);

  // Transfer categories for selected agent
  const agentTransferCategories = trpc.agents.getTransferConversationCategories.useQuery(
    { merchantId, agentId: selectedAgentId },
    {
      enabled: !!merchantId && !!selectedAgentId,
    }
  );

  // Mutation to update transfer categories
  const updateAgentTransferCategories =
    trpc.agents.updateTransferConversationCategories.useMutation({
      onSuccess: () => {
        toast.success("Categorias actualizadas", {
          description: "Las categorias de transferencia han sido actualizadas exitosamente",
        });
        utils.agents.getTransferConversationCategories.invalidate();
        utils.assignmentRulesets.getTransferCategories.invalidate();
      },
      onError: (error: { message?: string }) => {
        toast.error("Error", {
          description: error.message || "Hubo un error al actualizar las categorias",
        });
      },
    });

  const handleSaveAgentCategories = (categories: Array<{ name: string; description: string }>) => {
    updateAgentTransferCategories.mutate({
      merchantId,
      agentId: selectedAgentId,
      transferConversationCategories: categories,
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <HandoffManagement
      handoffData={{
        employees: employees.data || [],
        assignmentRulesets: assignmentRulesets.data || [],
      }}
      isLoading={assignmentRulesets.isLoading || employees.isLoading}
      merchantType={merchantType}
      onCreateEmployee={(name, phone, email, crmId, projectId) =>
        createEmployee.mutateAsync({ merchantId, name, phone, email, crmId, projectId })
      }
      onUpdateEmployee={(employeeId, updates) => {
        const payload: {
          merchantId: string;
          employeeId: string;
          name: string;
          phone: string;
          email?: string;
          crmId?: string;
          projectId?: string;
        } = {
          merchantId: merchantId!,
          employeeId,
          name: updates.name,
          phone: updates.phone,
          email: updates.email,
        };
        if (updates.crmId !== undefined) {
          payload.crmId = updates.crmId;
        }
        if (updates.projectId !== undefined) {
          payload.projectId = updates.projectId;
        }
        return updateEmployee.mutateAsync(payload);
      }}
      onToggleEmployeeStatus={(employeeId, status) =>
        toggleEmployeeStatus.mutateAsync({ merchantId, employeeId, status })
      }
      restrictEmployeeStatusChanges={restrictEmployeeStatusChanges}
      employeeStatusSuperEmails={employeeStatusSuperEmails}
      callerEmail={callerEmail}
      onCreateAssignmentRuleset={(name, ruleset, priority, ignorePastEmployee) =>
        createAssignmentRuleset.mutateAsync({
          merchantId,
          name,
          ruleset,
          priority,
          ignorePastEmployee,
        })
      }
      onUpdateAssignmentRuleset={(
        assignmentRulesetId,
        updates,
        name,
        priority,
        ignorePastEmployee
      ) =>
        updateAssignmentRuleset.mutateAsync({
          merchantId,
          assignmentRulesetId,
          ruleset: updates,
          name,
          priority,
          ignorePastEmployee,
        })
      }
      onAddEmployeesToAssignmentRuleset={(assignmentRulesetId, employeeIds) =>
        addEmployeesToAssignmentRuleset.mutateAsync({
          merchantId,
          assignmentRulesetId,
          employeeIds,
        })
      }
      onRemoveEmployeesFromAssignmentRuleset={(assignmentRulesetId, employeeIds) =>
        removeEmployeesFromAssignmentRuleset.mutateAsync({
          merchantId,
          assignmentRulesetId,
          employeeIds,
        })
      }
      cityNeighborhoods={cityNeighborhoods.data || []}
      transferCategories={transferCategories.data || []}
      branches={branches.data || []}
      pipelines={pipelines.data || []}
      agents={agents.data || []}
      isLoadingAgents={agents.isLoading}
      selectedAgentId={selectedAgentId}
      agentTransferCategories={agentTransferCategories.data || []}
      onAgentChange={setSelectedAgentId}
      isLoadingAgentCategories={agentTransferCategories.isLoading}
      onSaveAgentCategories={handleSaveAgentCategories}
      isSavingAgentCategories={updateAgentTransferCategories.isLoading}
    />
  );
};

export default AssignmentContainer;
